"use client";

import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useState,
} from "react";
import { createLogAdapter } from "@/lib/logger/console-adapter";
import type { Language } from "@/hooks/useLanguage";
import { translations } from "@/lib/translations";

interface LanguageContextType {
	language: Language;
	toggleLanguage: () => void;
	setLanguage: (lang: Language) => void;
	isLanguageManual: boolean;
	getTranslation: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
	undefined,
);
const uiLog = createLogAdapter("contexts/language-context.tsx");

export function LanguageProvider({ children }: { children: ReactNode }) {
	const [language, setLanguage] = useState<Language>("en");
	const [isLanguageManual, setIsLanguageManual] = useState(false);

	// Detect browser language and set initial language
	useEffect(() => {
		if (!isLanguageManual) {
			try {
				const browserLang = navigator.language.toLowerCase();
				if (browserLang.includes("zh")) {
					setLanguage("zh");
				} else {
					setLanguage("en");
				}
			} catch (error) {
				uiLog.error("Error detecting browser language:", error);
			}
		}
	}, [isLanguageManual]);

	// Load settings from local storage
	useEffect(() => {
		try {
			const savedLanguage = localStorage.getItem("language");
			if (savedLanguage && (savedLanguage === "zh" || savedLanguage === "en")) {
				setLanguage(savedLanguage as Language);
				setIsLanguageManual(true);
			}
		} catch (error) {
			uiLog.error("Error loading language from localStorage:", error);
		}
	}, []);

	// Set and save language
	const setLanguageAndSave = (lang: Language) => {
		try {
			setLanguage(lang);
			setIsLanguageManual(true);
			localStorage.setItem("language", lang);
		} catch (error) {
			uiLog.error("Error setting language:", error);
		}
	};

	// Toggle language
	const toggleLanguage = () => {
		try {
			const newLanguage = language === "zh" ? "en" : "zh";
			setLanguage(newLanguage);
			setIsLanguageManual(true);
			localStorage.setItem("language", newLanguage);
		} catch (error) {
			uiLog.error("Error toggling language:", error);
		}
	};

	const getTranslation = useCallback(
		(key: string): string => {
			const keys = key.split(".");
			let result: any = translations[language];
			for (const k of keys) {
				if (result === undefined) {
					return "";
				}
				result = result[k];
			}
			return result || "";
		},
		[language],
	);

	return (
		<LanguageContext.Provider
			value={{
				language,
				toggleLanguage,
				setLanguage: setLanguageAndSave,
				isLanguageManual,
				getTranslation,
			}}
		>
			{children}
		</LanguageContext.Provider>
	);
}

export function useLanguageContext() {
	const context = useContext(LanguageContext);
	if (context === undefined) {
		throw new Error(
			"useLanguageContext must be used within a LanguageProvider",
		);
	}
	return context;
}
